/**
 * Navigation rig: orbit / fly / walk / viewpoint-jump (parent spec §9.1).
 * DOM/input glue lives here; the decision logic it calls (ground lock,
 * viewpoint resolution, bearing maths) is in plain, DOM-free modules so it
 * can be unit-tested without a browser.
 */
import { PerspectiveCamera, Vector3 } from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { bearingDegFromForward, forwardFromBearingDeg, yawFromBearingDeg } from '../core/coordinates';
import type { RoutePolyline } from '../core/route';
import type { Viewpoint } from '../core/manifest';
import { EYE_HEIGHT_M, MIN_FLY_CLEARANCE_M, clampAboveGround, lockToGround, walkStep, type GroundSampler } from './groundLock';
import { poseForViewpoint, type CameraPose } from './viewpointJump';
import type { RoutePlayer, RouteAdvanceContext, RouteTarget } from './routePlayer';
import {
  EXPLORE_AZIMUTH_RATE,
  EXPLORE_MAX_POLAR_LEVEL,
  EXPLORE_MIN_POLAR,
  EXPLORE_TILT_RATE,
  exploreAltitudeDelta,
  exploreHeading,
  explorePanSpeed,
  exploreTiltCeiling,
  exploreTruckDelta,
  rotateAzimuthY,
  tiltOffset,
  type ExploreKeyState,
  type Vec3,
} from './exploreDrive';
import { DoubleTapTracker } from './doubleTap';

export type NavMode = 'orbit' | 'fly' | 'walk' | 'route' | 'explore';

/**
 * Static route data + the shared RoutePlayer instance driving it. The
 * player is created and owned by the caller (main.ts), which also reads
 * its public playback fields (distanceAlong, playing, etc.) directly to
 * drive the route control panel — this config just tells the rig what to
 * advance() against once in 'route' mode (design doc §"camera/cameraRig.ts").
 */
export interface RouteConfig {
  player: RoutePlayer;
  polyline: RoutePolyline;
  arrayCentreTarget: RouteTarget;
  turbineTargets: RouteTarget[];
  eyeHeightM?: number;
  speedMS?: number;
}

const FLY_SPEED_DEFAULT_M_S = 30;
const FLY_SPRINT_MULT = 4;
const WALK_SPEED_M_S = 4.5;
const WALK_SPRINT_MULT = 6; // Shift = jog/run, so the ~12 km AOI is traversable on foot
const MOUSE_SENSITIVITY = 0.0022;
const MAX_PITCH = 1.5;
// Arrow-key look rotation rates (radians/second), issue #67. Yaw a touch
// faster than pitch, matching typical look-around feel. Shared by Fly/Walk
// (added alongside mouse-look) and Route (its only look mechanism, having
// replaced the retired N/M/K/L keys at this same rate).
const ARROW_LOOK_YAW_RATE = 1.2;
const ARROW_LOOK_PITCH_RATE = 0.9;
// How far ahead of a teleport-landing camera (issue #158) the Explore orbit
// focus is seated. Deliberately not "at the camera" — see jumpToPoint's own
// comment for why that degenerate, near-zero radius destabilises against the
// fly/orbit floor lift. Small enough to still read as "focused on where you
// just landed", large enough to keep OrbitControls' spherical maths healthy.
const EXPLORE_JUMP_FOCUS_DISTANCE_M = 20;

const forwardVec = new Vector3();
const rightVec = new Vector3();
const moveVec = new Vector3();
// Hoisted for updateWalk (issue #131 / #17 finding 5), matching updateFly's
// existing idiom above: computed once per tick regardless of which keys are
// held, then added/subtracted into moveVec, so walking allocates nothing per
// frame. Kept distinct from fly's forwardVec/rightVec (yaw-plane only, never
// rotated by pitch — walking never tilts) rather than shared, so a future
// change to one mode's maths can't silently affect the other.
const walkForwardVec = new Vector3();
const walkRightVec = new Vector3();
// Explore-mode scratch (issue #142): the camera up vector (for the heading
// fallback when looking straight down) and a plain {x,y,z} offset reused each
// frame, so keyboard driving allocates no Vector3 per frame (issue #17), the
// same hoisted-scratch idiom as fly/walk above. Explore is mutually exclusive
// with fly/walk/route, so sharing a single set of scratches is safe.
const exploreUpVec = new Vector3();
const exploreOffsetScratch: Vec3 = { x: 0, y: 0, z: 0 };
const exploreKeyScratch: ExploreKeyState = { forward: false, back: false, left: false, right: false };
// Reused across frames for the fly/orbit terrain-floor check (issue #50) — a
// plain {x,y,z} scratch matching groundLock's engine-agnostic pure functions,
// so the render loop allocates nothing in the common case where the camera is
// already above the floor. Fly and orbit are mutually exclusive modes, so a
// single scratch is safe to share.
const floorScratch = { x: 0, y: 0, z: 0 };

export class CameraRig {
  readonly camera: PerspectiveCamera;
  // Boots in Explore — the community-facing default nav mode (issue #143).
  // Explore reuses Orbit's OrbitControls drag/zoom setup (enabled by default in
  // the constructor) and, with no keys held, its per-frame update is identical
  // to Orbit's; the explore-specific polar clamp is asserted on an explicit
  // setMode('explore') entry, not at construction, so the boot pose (and the
  // boot intro's overhead hold) are left exactly as before. main.ts switches to
  // a different first-enabled mode when the config names one.
  mode: NavMode = 'explore';
  // Bumped by a successful jumpToPoint (issue #158). CameraRig has no idea
  // main.ts runs a boot intro that forces camera.position every frame while
  // active (LOS/UI layering: this module never reaches upward into main.ts
  // concepts) — but main.ts's intro-cancellation check used to rely purely
  // on `rig.mode !== bootNavMode` to detect "something else took the
  // camera", and a jump landing in Explore (the intro's own boot mode) no
  // longer trips that check, so a jump mid-intro would silently get
  // overwritten by the intro's next tick. Monotonically increasing (not a
  // boolean) so a caller can detect "changed since I last looked" purely by
  // comparing snapshots, without racing a second reader that already
  // consumed a one-shot flag.
  jumpSeq = 0;
  /**
   * How far above the terrain the Explore pivot currently sits, in metres
   * (issue #184). Zero means "seated on the surface", which is what every
   * arrival into Explore resets it to (mode entry, teleport, viewpoint jump,
   * located pose — see resetExploreAltitude).
   *
   * Shift+E/Q drives this, and every place the Explore pivot is seated seats it
   * to `ground + this` rather than to `ground`. That is what makes the elevator
   * a real climb: camera and pivot move by the same delta, so the camera→pivot
   * vector — and therefore the polar angle and orbit radius OrbitControls
   * re-derives from it — is unchanged and no clamp fires. Plain E/Q moves only
   * the camera, which swings the eye over a pinned pivot (a tilt and a
   * dolly-out); that motion is deliberately kept as it was.
   *
   * Read-only for callers outside the rig; e2e reads it as a test hook.
   */
  exploreAltitudeOffsetM = 0;
  groundSampler: GroundSampler = () => null;
  private routeConfig: RouteConfig | null = null;
  /**
   * Explore's tilt ceiling for the current frame (issue #186): the largest
   * polar angle that still leaves the eye above the camera floor, derived by
   * exploreTiltCeiling and applied to BOTH the keyboard tilt (tiltOffset) and
   * OrbitControls' own drag clamp, so the two can never disagree. Starts at —
   * and falls back to — the static level ceiling.
   */
  private exploreMaxPolar = EXPLORE_MAX_POLAR_LEVEL;
  /**
   * Ground elevation under the camera as of the last applyCameraFloor call, or
   * null when the sampler had no data there. This is the sample the tilt
   * ceiling is derived from: reusing it is what keeps the ceiling free of any
   * new per-frame collider query (hard constraint #17). It is therefore one
   * frame stale, which is acceptable precisely because applyCameraFloor stays
   * the backstop — but it is invalidated outright on every arrival, since after
   * a teleport it describes a different hillside entirely.
   */
  private cameraGroundY: number | null = null;
  /**
   * Whether Explore's polar clamp has been asserted (issue #186). The rig boots
   * straight into Explore with OrbitControls left unclamped so the boot intro
   * can hold dead-overhead (#86/#143); main.ts asserts the clamp only once the
   * user cancels the intro. Until then updateExplore must not write a ceiling
   * either — the intro's authored poses are not this clamp's business.
   */
  private exploreClampActive = false;
  /**
   * The sampler applyCameraFloor is given: this.groundSampler, plus a note of
   * what it returned under the camera (issue #186). Bound once, as a field
   * rather than a per-call closure, so the render loop still allocates nothing.
   * The floor's own behaviour is untouched — it is exactly the sampler it would
   * otherwise have used — and this is the whole of "reuse the sample the floor
   * already takes" rather than adding a second query (hard constraint #17).
   */
  private readonly recordCameraGround: GroundSampler = (x, z) => {
    const ground = this.groundSampler(x, z);
    this.cameraGroundY = ground;
    return ground;
  };

  private readonly domElement: HTMLElement;
  private readonly orbitControls: OrbitControls;
  // Horizontal position at which the orbit pivot was last seated on the
  // ground. NaN forces a (re)seat next orbit frame. Tracking x/z (not y) means
  // seatOrbitTargetIfNeeded only spends a sampleGround call when the target has
  // actually moved horizontally (a pan) — idle orbit costs zero extra samples.
  private orbitTargetGroundX = Number.NaN;
  private orbitTargetGroundZ = Number.NaN;
  private yaw = 0;
  private pitch = 0;
  private flySpeed = FLY_SPEED_DEFAULT_M_S;
  private readonly keys = new Set<string>();
  private pointerLocked = false;
  private readonly doubleTapTracker = new DoubleTapTracker();
  // Double-click/double-tap teleport (issue #156): set by the caller once it
  // has a terrain-surface picker wired up (main.ts, via the collider). Fires
  // with client (viewport-pixel) coordinates on a qualifying gesture in
  // Explore/Orbit only; fly/walk's single-click pointer-lock behaviour above
  // is untouched. `null`/unset means "no-op" — never a fabricated pick.
  onSurfaceDoubleClick: ((clientX: number, clientY: number) => void) | null = null;

  constructor(camera: PerspectiveCamera, domElement: HTMLElement) {
    this.camera = camera;
    this.domElement = domElement;
    this.orbitControls = new OrbitControls(camera, domElement);
    this.orbitControls.enableDamping = true;
    this.orbitControls.dampingFactor = 0.08;
    this.bindInput();
  }

  private bindInput(): void {
    const dom = this.domElement;
    dom.addEventListener('click', () => {
      if (this.mode !== 'fly' && this.mode !== 'walk') return;
      if (!this.pointerLocked) dom.requestPointerLock?.();
    });
    // Desktop double-click teleport (issue #156): only in the cursor-driven
    // nav modes. Fly/walk are pointer-locked (no cursor to pick with) and
    // Route has no click-to-teleport in this issue's scope.
    dom.addEventListener('dblclick', (e) => {
      if (this.mode !== 'explore' && this.mode !== 'orbit') return;
      this.onSurfaceDoubleClick?.(e.clientX, e.clientY);
    });
    // Touch double-tap teleport, same gating. Single-finger only: a second
    // finger joining mid-gesture (pinch) cancels via doubleTapTracker.cancel().
    dom.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) {
          this.doubleTapTracker.cancel();
          return;
        }
        const t = e.touches[0];
        this.doubleTapTracker.start({ x: t.clientX, y: t.clientY, time: performance.now() });
      },
      { passive: true },
    );
    dom.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length !== 1) return;
        const t = e.touches[0];
        this.doubleTapTracker.move(t.clientX, t.clientY);
      },
      { passive: true },
    );
    dom.addEventListener(
      'touchend',
      (e) => {
        if (this.mode !== 'explore' && this.mode !== 'orbit') {
          this.doubleTapTracker.cancel();
          return;
        }
        // The lifted finger is in changedTouches, not touches.
        const t = e.changedTouches[0];
        if (!t) return;
        const tap = this.doubleTapTracker.end({ x: t.clientX, y: t.clientY, time: performance.now() });
        if (tap) this.onSurfaceDoubleClick?.(tap.x, tap.y);
      },
      { passive: true },
    );
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === dom;
    });
    document.addEventListener('mousemove', (e) => {
      if (!this.pointerLocked) return;
      this.yaw -= e.movementX * MOUSE_SENSITIVITY;
      this.pitch -= e.movementY * MOUSE_SENSITIVITY;
      this.pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, this.pitch));
    });
    window.addEventListener('keydown', (e) => {
      this.keys.add(e.code);
      // Fly speed via - / + (and numpad), so a trackpad without a wheel can
      // still change speed. Wheel remains supported below for mice.
      if (this.mode === 'fly') {
        if (e.code === 'Minus' || e.code === 'NumpadSubtract') this.adjustFlySpeed(-1);
        else if (e.code === 'Equal' || e.code === 'NumpadAdd') this.adjustFlySpeed(1);
      }
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());
    dom.addEventListener(
      'wheel',
      (e) => {
        if (this.mode !== 'fly') return;
        e.preventDefault();
        this.adjustFlySpeed(-Math.sign(e.deltaY));
      },
      { passive: false },
    );
  }

  /** Scale fly speed by 1.15^steps, clamped to [0.5, 2000] m/s. */
  private adjustFlySpeed(steps: number): void {
    if (steps === 0) return;
    this.flySpeed = Math.min(2000, Math.max(0.5, this.flySpeed * Math.pow(1.15, steps)));
  }

  setMode(mode: NavMode): void {
    if (mode === this.mode) return;
    // Explore is the only mode that constrains the orbit polar range (below);
    // (re)assert OrbitControls' default range on entry to every other mode so a
    // prior Explore session never leaks its clamp into orbit. These are already
    // orbit's defaults, so this is a no-op for orbit's own behaviour.
    this.orbitControls.minPolarAngle = 0;
    this.orbitControls.maxPolarAngle = Math.PI;
    this.exploreClampActive = false;
    this.invalidateExploreCeiling();
    if (mode === 'orbit') {
      this.orbitControls.enabled = true;
      // Re-seat the pivot on the terrain on entry: the target may carry a
      // stale y from before, and the camera may have flown elsewhere. Forcing
      // a reseat keeps the pivot on the ground so tilting arcs the camera
      // around the surface, not a buried point (issue #50 root cause).
      this.orbitTargetGroundX = Number.NaN;
      this.orbitTargetGroundZ = Number.NaN;
    } else if (mode === 'explore') {
      // Explore reuses orbit's drag/zoom setup — OrbitControls enabled, pivot
      // reseated on the terrain on entry (issue #142) — and layers keyboard
      // driving of the focus on top in updateExplore(). Deliberately mirrors
      // the orbit branch above rather than merging with it, so orbit's own
      // behaviour (and its tests) stay byte-for-byte untouched.
      //
      // Constrain the polar range so the camera is never exactly overhead: at
      // the top-down boot pose the camera sits straight above the focus, and
      // azimuth (←/→) rotates the camera→focus offset about the vertical axis —
      // a geometric no-op when that offset is purely vertical. A small minimum
      // tilt (EXPLORE_MIN_POLAR) keeps ←/→ responsive. OrbitControls clamps to
      // this range every update(), so drag-tilt honours it too. The maximum
      // starts at the static level ceiling and is replaced every frame by the
      // terrain-derived one (issue #186, updateExplore): the eye may go below
      // the focus's horizontal plane, but only as far as the ground beneath it
      // leaves room for.
      this.orbitControls.enabled = true;
      this.orbitControls.minPolarAngle = EXPLORE_MIN_POLAR;
      this.orbitControls.maxPolarAngle = EXPLORE_MAX_POLAR_LEVEL;
      this.exploreClampActive = true;
      this.orbitTargetGroundX = Number.NaN;
      this.orbitTargetGroundZ = Number.NaN;
      // Arrive seated on the terrain, never at a stale altitude from a previous
      // Explore session (issue #184).
      this.resetExploreAltitude();
    } else {
      this.orbitControls.enabled = false;
      this.applyRotation();
      if (mode === 'walk') {
        const pos = this.camera.position;
        const locked = lockToGround(pos.x, pos.z, this.groundSampler);
        if (locked) this.camera.position.set(locked.x, locked.y, locked.z);
      } else if (mode === 'route') {
        // Place the camera at the route's current playback position
        // immediately, rather than waiting a frame for the first update(dt).
        this.updateRoute(0);
      }
    }
    this.mode = mode;
  }

  /**
   * (Re)asserts Explore's OrbitControls polar clamp when the rig is already in
   * Explore — a no-op in any other mode. Needed because the rig boots straight
   * into Explore (issue #143), so `setMode('explore')` would early-return and
   * never run the clamp that keeps ←/→ azimuth responsive at a near-overhead
   * pose (the #142 azimuth-dead-zone). main.ts calls this the moment the user
   * cancels the boot intro — i.e. when they start driving, often from the
   * intro's overhead hold. It deliberately does NOT run during the intro or on
   * a natural settle: the intro must hold dead-overhead unclamped, and its
   * settled end pose sits just outside the clamp's max tilt, so asserting the
   * clamp there would nudge the pose (the boot-intro e2e asserts it). The clamp
   * itself never moves the camera; the next orbitControls.update() enforces it.
   */
  applyExploreClampIfActive(): void {
    if (this.mode !== 'explore') return;
    this.orbitControls.minPolarAngle = EXPLORE_MIN_POLAR;
    // The static level ceiling (issue #186); updateExplore replaces it with the
    // terrain-derived one from the next frame on. Deliberately NOT the dynamic
    // value here: this runs on arrivals, where the recorded ground sample
    // belongs to wherever the camera was before.
    this.orbitControls.maxPolarAngle = EXPLORE_MAX_POLAR_LEVEL;
    this.exploreClampActive = true;
    this.invalidateExploreCeiling();
  }

  /**
   * Forgets the ground sample the tilt ceiling is derived from and puts the
   * ceiling back to the static level value (issue #186). Called on every
   * arrival — mode entry, teleport, viewpoint jump, located pose — because the
   * recorded sample is the ground under where the camera *was*, and offering
   * past-level tilt on the strength of it after a jump would be tilting against
   * ground that has not been verified (hard constraint #7). The next frame's
   * own floor sample restores the dynamic ceiling.
   */
  private invalidateExploreCeiling(): void {
    this.cameraGroundY = null;
    this.exploreMaxPolar = EXPLORE_MAX_POLAR_LEVEL;
  }

  /**
   * Puts the Explore pivot back on the terrain (issue #184): clears the
   * altitude offset and forces the next per-frame seat to re-ground the pivot,
   * which it otherwise wouldn't — seatOrbitTargetIfNeeded only re-seats when
   * the pivot has moved horizontally, and the pivot is currently sitting at
   * `ground + the offset just cleared`.
   *
   * Called on every arrival into Explore. `setMode('explore')` is not enough on
   * its own: it early-returns when the requested mode is already the current
   * one, and every teleport/jump path can be taken from inside Explore (the
   * same gap `applyExploreClampIfActive` exists to cover).
   */
  private resetExploreAltitude(): void {
    if (this.exploreAltitudeOffsetM === 0) return;
    this.exploreAltitudeOffsetM = 0;
    this.orbitTargetGroundX = Number.NaN;
    this.orbitTargetGroundZ = Number.NaN;
  }

  /**
   * Wires the pre-programmed route fly-through (design doc). Route mode is
   * interactive/visual only, never LOS truth (hard constraint #1) — it
   * exists purely so a community viewer can be walked along a real route
   * while looking at the turbine array; nothing here feeds any LOS result.
   */
  setRoute(config: RouteConfig): void {
    this.routeConfig = config;
  }

  /**
   * Explicitly (re)seats the orbit pivot's horizontal position (issue #86:
   * the boot intro anchors the pivot at the default viewpoint, read from
   * config, rather than assuming the scene origin). Only ever sets x/z —
   * the pivot's vertical position stays owned by seatOrbitTargetIfNeeded
   * (issue #50), never set directly here (hard constraint #7: vertical
   * placement only ever comes from the collider). Forces a ground reseat on
   * the next orbit-mode update(), same as setMode('orbit').
   */
  setOrbitPivotXZ(x: number, z: number): void {
    this.orbitControls.target.set(x, this.orbitControls.target.y, z);
    this.orbitTargetGroundX = Number.NaN;
    this.orbitTargetGroundZ = Number.NaN;
  }

  private applyRotation(): void {
    this.camera.rotation.set(0, 0, 0);
    this.camera.rotateY(this.yaw);
    this.camera.rotateX(this.pitch);
  }

  /**
   * Arrow-key yaw/pitch deltas for this tick (issue #67), or {0,0} if none
   * of the four arrow keys are held. Sign matches mouse-look (left/up
   * positive) so arrow-key and mouse-look feel identical in Fly/Walk.
   */
  private arrowLookDelta(dt: number): { dYaw: number; dPitch: number } {
    let dYaw = 0;
    let dPitch = 0;
    if (this.keys.has('ArrowLeft')) dYaw += ARROW_LOOK_YAW_RATE * dt;
    if (this.keys.has('ArrowRight')) dYaw -= ARROW_LOOK_YAW_RATE * dt;
    if (this.keys.has('ArrowUp')) dPitch += ARROW_LOOK_PITCH_RATE * dt;
    if (this.keys.has('ArrowDown')) dPitch -= ARROW_LOOK_PITCH_RATE * dt;
    return { dYaw, dPitch };
  }

  /**
   * Applies arrow-key look to yaw/pitch (Fly/Walk, issue #67) — additive to
   * whatever mouse-look already did this tick, clamped the same as mouse
   * pitch so arrows can't tip the view past straight up/down.
   */
  private applyArrowLook(dt: number): void {
    const { dYaw, dPitch } = this.arrowLookDelta(dt);
    if (dYaw === 0 && dPitch === 0) return;
    this.yaw += dYaw;
    this.pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, this.pitch + dPitch));
  }

  /** Bearing of the current camera-forward direction, compass degrees. */
  getBearingDeg(): number {
    this.camera.getWorldDirection(forwardVec);
    return bearingDegFromForward(forwardVec.x, forwardVec.z);
  }

  /** Jump the camera to a saved viewpoint's BNG position + eye height + bearing.
   * A viewpoint with no fov_deg preserves the camera's current vertical FOV
   * (the active view-scale preset, issue #95) rather than forcing a fixed
   * value — poseForViewpoint only reads this.camera.fov to cover that case.
   *
   * `mode` is the mode to land in, defaulting to 'fly' (the original issue #75
   * behaviour). Issue #183 has main.ts pass the mode the viewer is already in,
   * so a selection no longer drops them out of the mode they were using — into
   * Fly, which the community-facing default toolbar doesn't even render
   * (#143). A landing in one of the OrbitControls-driven modes (explore/orbit)
   * seats the orbit focus exactly the way `jumpToPoint` does, including its
   * #158 degenerate-radius guard, but takes the direction from the viewpoint's
   * own `bearing_deg` rather than from wherever the camera happened to be
   * looking — the viewpoint's bearing is the whole point of jumping to it.
   *
   * Throws (leaving the camera untouched) for a viewpoint with no resolved
   * coordinate, no loaded terrain under it, or an out-of-range fov_deg; see
   * poseForViewpoint. jumpSeq is bumped only on success. */
  jumpToViewpoint(viewpoint: Viewpoint, mode: NavMode = 'fly'): void {
    const pose = poseForViewpoint(viewpoint, this.groundSampler, this.camera.fov);
    // Before anything is seated: an arriving user starts on the terrain at the
    // destination, never at the altitude they had climbed to over the old one
    // (issue #184). Runs whatever the landing mode, so a later return to
    // Explore isn't pre-lifted either.
    this.resetExploreAltitude();
    this.camera.position.set(pose.x, pose.y, pose.z);
    this.camera.fov = pose.fovDeg;
    this.camera.updateProjectionMatrix();
    this.yaw = yawFromBearingDeg(pose.bearingDeg);
    this.pitch = 0;
    this.setMode(mode);
    if (mode === 'explore' || mode === 'orbit') {
      // pose.y is ground + eye height (poseForViewpoint), so the ground under
      // the camera is recoverable exactly — no second collider query for it.
      const groundY = pose.y - (viewpoint.eye_height_m ?? EYE_HEIGHT_M);
      this.seatJumpFocus(pose.x, pose.z, groundY, forwardFromBearingDeg(pose.bearingDeg));
      // A jump that was already in explore never entered setMode's explore
      // branch, so the polar clamp needs reasserting here (same reasoning as
      // jumpToPoint's own call). A no-op in orbit, which is never clamped.
      this.applyExploreClampIfActive();
    } else {
      this.applyRotation();
    }
    this.camera.updateMatrixWorld();
    // The intro-pre-emption signal (see jumpSeq's own comment): a
    // mode-preserving jump leaves rig.mode untouched, so this counter is the
    // ONLY thing that tells main.ts the boot intro has been pre-empted and
    // must stop overwriting the camera.
    this.jumpSeq++;
  }

  /**
   * Records that the camera's destination has just been changed by something
   * this rig did not itself do — issue #183's Drive-mode viewpoint selection
   * moves route playback (RoutePlayer.distanceAlong), which the rig then
   * follows on its next updateRoute tick, so no jumpTo* method runs. Bumping
   * the same counter keeps the boot intro's pre-emption check (main.ts) honest
   * for that path too.
   */
  signalJump(): void {
    this.jumpSeq++;
  }

  /**
   * Seats the OrbitControls focus a short distance ahead of a just-landed
   * camera, grounded synchronously. Shared by jumpToPoint and jumpToViewpoint;
   * see jumpToPoint's comment for the two #158 regressions this shape exists
   * to avoid (a focus at the camera gives a degenerate near-vertical orbit
   * radius; a focus left ungrounded gets its first clamp pass computed against
   * a stale pivot height). `groundY` is the already-known ground elevation
   * under the camera, used only as the fallback when the point ahead has no
   * loaded tile — never a fabricated height (hard constraints #6/#7).
   */
  private seatJumpFocus(x: number, z: number, groundY: number, heading: { x: number; z: number }): void {
    const focusX = x + heading.x * EXPLORE_JUMP_FOCUS_DISTANCE_M;
    const focusZ = z + heading.z * EXPLORE_JUMP_FOCUS_DISTANCE_M;
    // A second collider query, deliberately: this is an event (a jump), not
    // a per-frame cost (the render-loop amortisation rule only covers the
    // latter).
    const focusGround = this.groundSampler(focusX, focusZ);
    this.orbitControls.target.set(focusX, focusGround !== null ? focusGround : groundY, focusZ);
    this.orbitTargetGroundX = focusX;
    this.orbitTargetGroundZ = focusZ;
  }

  /**
   * Jump the camera to a "My location" GPS-derived pose (issue #161): the
   * same end-state shape as `jumpToViewpoint` above — fly mode, bearing,
   * camera FOV — but sourced from `poseForMyLocation` instead of a saved
   * viewpoint. `pose.y` is already collider-sampled by the caller
   * (`poseForMyLocation` itself refuses to fabricate a ground height), so
   * this never re-samples the ground.
   *
   * `mode` defaults to 'fly' (unchanged desktop behaviour) but the caller
   * may request a different landing mode — issue #166: a touch-only
   * viewport hides Fly/Walk entirely (issue #108's `#hud[data-touch-only]`
   * control set), so landing there in fly stranded the camera in a mode
   * with no working touch controls, appearing frozen. Callers detect
   * touch-only the same way the mode buttons do (`createTouchOnlySignal`)
   * and pass 'explore' in that case; the double-tap teleport
   * (`jumpToPoint`, issues #156/#158) is untouched by this parameter.
   */
  jumpToLocatedPose(pose: CameraPose, mode: NavMode = 'fly'): void {
    this.resetExploreAltitude(); // land seated on the terrain (issue #184)
    // Explicit here because setMode early-returns on a landing taken in the
    // mode it lands in, so mode entry's own invalidation never runs (issue
    // #186; the same gap resetExploreAltitude is called here to cover).
    this.invalidateExploreCeiling();
    this.camera.position.set(pose.x, pose.y, pose.z);
    this.camera.fov = pose.fovDeg;
    this.camera.updateProjectionMatrix();
    this.yaw = yawFromBearingDeg(pose.bearingDeg);
    this.pitch = 0;
    this.setMode(mode);
    this.applyRotation();
    this.camera.updateMatrixWorld();
  }

  /**
   * Jump the camera to a horizontal world position (minimap click-to-jump,
   * issue #38; also the double-click teleport, issue #156), placed at
   * ground + eye height via the collider-backed sampler (hard constraint
   * #6: vertical placement only ever comes from the DTM-derived collider) —
   * the camera physically STANDS at the destination, same as fly always did,
   * so anything keyed off the camera's own position (e.g. the near-field
   * tree-mesh realisation radius, issue #92) sees it there immediately.
   * Lands in Explore (issue #158) — the same landing mode regardless of
   * which mode the jump was initiated from, so a chained double-click
   * teleport never disables itself the way landing in fly used to — with
   * the orbit focus seated a short distance ahead, along whatever direction
   * the camera was already facing. The focus is deliberately NOT placed at
   * the camera itself: that gives OrbitControls a near-zero, nearly-vertical
   * orbit radius, and its polar clamp then fights CameraRig's independent
   * fly/orbit floor lift (issue #50) every frame over that degenerate
   * geometry, walking the camera away from the landing spot on live runs
   * (the treeMeshBootGate regression this fix corrects). A real, mostly-
   * horizontal radius keeps that maths well-conditioned and stable. If the
   * jump releases a pointer-locked fly/walk session, the lock is dropped
   * explicitly rather than left dangling with no mode that uses it. Returns
   * false — camera untouched — if no terrain is loaded at the target yet:
   * never place the camera at an unresolved ground height.
   *
   * The orbit focus is grounded HERE, synchronously, rather than left for
   * the next per-frame seatOrbitTargetIfNeeded to pick up (how every other
   * pivot move in this file — WASD truck, setMode('orbit') entry — works).
   * updateExplore() calls orbitControls.update() BEFORE seatOrbitTargetIfNeeded
   * every frame, so a pivot left ungrounded across a jump gets its very
   * first clamp/update pass computed against a stale target.y (wherever the
   * pivot sat before, e.g. the boot pose's ~0 m AOD) against the new
   * camera's real ~250-380 m AOD landing height — a huge, spurious, mostly-
   * vertical offset that the polar clamp then "corrects" toward whatever
   * direction that bad geometry implies, nowhere near the actual landing
   * point (the live treeMeshBootGate regression this fix corrects: the
   * camera ended up tens to hundreds of metres from the destination).
   * Grounding synchronously means the first real update() ever sees a
   * correct, well-conditioned offset.
   */
  jumpToPoint(x: number, z: number): boolean {
    const locked = lockToGround(x, z, this.groundSampler);
    if (!locked) return false;
    if (this.pointerLocked) document.exitPointerLock?.();
    // Land seated on the terrain (issue #184). Explicit here because the
    // setMode('explore') below early-returns on a teleport taken while already
    // in Explore, so mode entry's own reset never runs on that path.
    this.resetExploreAltitude();
    // Read the camera's current horizontal look direction before touching
    // anything else, the same "keep the bearing" idea the old orbit->fly
    // branch used — falls back to exploreHeading's own up-vector fallback,
    // and finally to due north, for a dead-overhead pose with no derivable
    // horizontal heading at all.
    this.camera.getWorldDirection(forwardVec);
    exploreUpVec.set(0, 1, 0).applyQuaternion(this.camera.quaternion);
    const heading = exploreHeading(forwardVec, exploreUpVec) ?? { x: 0, z: -1 };
    this.camera.position.set(locked.x, locked.y, locked.z);
    this.setMode('explore');
    // Never fabricate the focus height (#6/#7) — seatJumpFocus falls back to
    // the camera's own ground height (already sampled by lockToGround) if the
    // point 20 m ahead hasn't streamed in yet.
    this.seatJumpFocus(locked.x, locked.z, locked.y - EYE_HEIGHT_M, heading);
    // setMode('explore') only asserts the polar clamp on an actual mode
    // change; a jump that was already in Explore needs it reasserted here
    // (mirrors applyExploreClampIfActive's own doc comment).
    this.applyExploreClampIfActive();
    this.camera.updateMatrixWorld();
    this.jumpSeq++;
    return true;
  }

  update(dt: number): void {
    if (this.mode === 'orbit') {
      this.orbitControls.update();
      this.seatOrbitTargetIfNeeded();
      this.applyCameraFloor();
      return;
    }
    if (this.mode === 'explore') this.updateExplore(dt);
    else if (this.mode === 'fly') this.updateFly(dt);
    else if (this.mode === 'walk') this.updateWalk(dt);
    else if (this.mode === 'route') this.updateRoute(dt);
  }

  /**
   * Explore mode (issue #142): orbit's exact drag/zoom feel plus keyboard
   * driving of the orbit focus. The keyboard driver runs first (trucking the
   * focus with WASD, Q/E raise/lower, ←/→ azimuth, ↑/↓ tilt), then the same
   * three steps the orbit branch runs — OrbitControls damping/drag, reseat the
   * pivot on the terrain, apply the camera floor. Orbit is left entirely
   * unchanged; this is a sibling path so the two can be compared side by side.
   *
   * Ground sampling is amortised (hard constraint #17): the truck spends at
   * most one sampleGround per frame while a WASD key is held (and records the
   * result so seatOrbitTargetIfNeeded doesn't re-sample the same point), Q/E
   * spends the applyCameraFloor sample that already runs every orbit frame, and
   * an idle explore frame samples nothing — the pivot hasn't moved. The one
   * addition is Shift+Q (the altitude elevator descending, issue #184), which
   * spends a single sample to confirm the terrain under the pivot is real
   * before it drops toward it; Shift+E spends none.
   */
  private updateExplore(dt: number): void {
    this.refreshExploreTiltCeiling();
    this.applyExploreTruck(dt);
    this.applyExploreAzimuthTilt(dt);
    this.applyExploreHeight(dt);
    this.applyExploreAltitude(dt);
    // Written BEFORE update(), which is what enforces it on a drag — the same
    // value the keyboard tilt above was given, so the two can never disagree
    // (issue #186). Only once Explore's clamp has been asserted: before that
    // the boot intro owns the pose and is deliberately unclamped.
    if (this.exploreClampActive) this.orbitControls.maxPolarAngle = this.exploreMaxPolar;
    this.orbitControls.update();
    this.seatOrbitTargetIfNeeded();
    this.applyCameraFloor();
  }

  /**
   * Recomputes Explore's tilt ceiling for this frame (issue #186) from the
   * ground sample applyCameraFloor already took last frame, the pivot's current
   * height (`ground + exploreAltitudeOffsetM` since #184) and the current orbit
   * radius. Costs no collider query at all (hard constraint #17).
   *
   * The formula is circular in principle — where the ground under the camera is
   * depends on where the tilt puts the camera — and is resolved by using last
   * frame's sample. The resulting one-frame lag is safe because the ceiling
   * only ever removes tilt the camera has not taken yet, and applyCameraFloor
   * remains the backstop for the metre or two a fast truck can outrun.
   */
  private refreshExploreTiltCeiling(): void {
    const t = this.orbitControls.target;
    this.exploreMaxPolar = exploreTiltCeiling(
      this.cameraGroundY,
      t.y,
      this.camera.position.distanceTo(t),
      MIN_FLY_CLEARANCE_M,
    );
  }

  /**
   * WASD trucks the orbit focus across the terrain, view-relative (camera
   * forward projected to the ground). Both the focus (orbit target) and the
   * camera are translated by the same horizontal delta so the view angle is
   * preserved, and the camera also rides the vertical change of the terrain
   * under the focus so the orbit offset stays constant over hills.
   *
   * The focus is kept seated on the terrain: the destination height comes from
   * the collider sampler, never invented. If the sampler has no data at the
   * destination (off-AOI / unloaded tile) the move is rejected outright and the
   * focus stays at the last grounded position (hard constraints #6/#7). The
   * truck also waits until the pivot is confirmed seated before moving, so the
   * vertical ride below is measured against a real height rather than a stale
   * one — an invisible one-frame delay on entry.
   */
  private applyExploreTruck(dt: number): void {
    const forward = this.keys.has('KeyW');
    const back = this.keys.has('KeyS');
    const left = this.keys.has('KeyA');
    const right = this.keys.has('KeyD');
    if (!forward && !back && !left && !right) return;

    const t = this.orbitControls.target;
    const seated = t.x === this.orbitTargetGroundX && t.z === this.orbitTargetGroundZ && Number.isFinite(t.y);
    if (!seated) return; // pivot not yet grounded this session — truck next frame

    this.camera.getWorldDirection(forwardVec);
    exploreUpVec.set(0, 1, 0).applyQuaternion(this.camera.quaternion);
    const heading = exploreHeading(forwardVec, exploreUpVec);
    if (heading === null) return; // no derivable ground heading — don't move

    exploreKeyScratch.forward = forward;
    exploreKeyScratch.back = back;
    exploreKeyScratch.left = left;
    exploreKeyScratch.right = right;
    const speed = explorePanSpeed(this.camera.position.distanceTo(t));
    const delta = exploreTruckDelta(heading, exploreKeyScratch, speed, dt);
    if (delta === null) return;

    const newX = t.x + delta.x;
    const newZ = t.z + delta.z;
    const ground = this.groundSampler(newX, newZ);
    if (ground === null) return; // stop at the terrain edge — never a fabricated height

    // Seat at ground + the altitude offset, not at bare ground (issue #184):
    // deriving dyTarget from `ground` alone would compute a large negative ride
    // on the first W press after a Shift+E climb and slam the camera back down,
    // silently wiping the offset. The offset itself is unchanged by trucking —
    // the pivot rides the terrain at a constant height above it.
    const seatY = ground + this.exploreAltitudeOffsetM;
    const dyTarget = seatY - t.y;
    this.camera.position.x += delta.x;
    this.camera.position.z += delta.z;
    this.camera.position.y += dyTarget;
    t.x = newX;
    t.z = newZ;
    t.y = seatY;
    // Record the freshly-seated point so seatOrbitTargetIfNeeded treats it as
    // already grounded and spends no second sampleGround this frame.
    this.orbitTargetGroundX = newX;
    this.orbitTargetGroundZ = newZ;
  }

  /**
   * ←/→ rotate the camera around the focus about the Y axis (azimuth); ↑/↓
   * change the polar angle (tilt): ↑ drops the eye toward the focus's
   * horizontal plane so the view tilts up toward the horizon, ↓ lifts it toward
   * a bird's-eye. Both act on the camera→focus offset; OrbitControls.update()
   * (called right after in updateExplore) re-derives its spherical state from
   * the moved camera and re-clamps it, so drag and keyboard stay consistent.
   */
  private applyExploreAzimuthTilt(dt: number): void {
    const az =
      (this.keys.has('ArrowLeft') ? EXPLORE_AZIMUTH_RATE * dt : 0) +
      (this.keys.has('ArrowRight') ? -EXPLORE_AZIMUTH_RATE * dt : 0);
    const tilt =
      (this.keys.has('ArrowUp') ? EXPLORE_TILT_RATE * dt : 0) +
      (this.keys.has('ArrowDown') ? -EXPLORE_TILT_RATE * dt : 0);
    if (az === 0 && tilt === 0) return;

    const t = this.orbitControls.target;
    exploreOffsetScratch.x = this.camera.position.x - t.x;
    exploreOffsetScratch.y = this.camera.position.y - t.y;
    exploreOffsetScratch.z = this.camera.position.z - t.z;
    let offset: Vec3 = exploreOffsetScratch;
    if (az !== 0) offset = rotateAzimuthY(offset, az);
    // This frame's ceiling (issue #186), the same value handed to
    // OrbitControls' own clamp in updateExplore — keyboard and drag stop at
    // exactly the same angle.
    if (tilt !== 0) offset = tiltOffset(offset, tilt, EXPLORE_MIN_POLAR, this.exploreMaxPolar);
    this.camera.position.set(t.x + offset.x, t.y + offset.y, t.z + offset.z);
  }

  /**
   * Q/E lower/raise the camera height above the focus (E up, Q down — the same
   * convention updateFly uses), scaled by zoom distance like the truck. The
   * below-terrain clamp is left to applyCameraFloor, which runs every explore
   * frame, so lowering can never sink the eye under the surface (issue #50).
   */
  private applyExploreHeight(dt: number): void {
    if (this.exploreShiftHeld()) return; // Shift+E/Q is the altitude elevator below
    const up = this.keys.has('KeyE');
    const down = this.keys.has('KeyQ');
    if (up === down) return; // neither held, or both cancelling
    const speed = explorePanSpeed(this.camera.position.distanceTo(this.orbitControls.target));
    this.camera.position.y += (up ? 1 : -1) * speed * dt;
  }

  /** True while either Shift key is held. In Explore this selects the altitude
   * elevator over the plain E/Q swing; Fly and Walk read the same keys as
   * their sprint modifier and are untouched by Explore's use of it. */
  private exploreShiftHeld(): boolean {
    return this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
  }

  /**
   * Shift+E / Shift+Q: a true altitude elevator (issue #184). Camera AND pivot
   * translate by the same delta at the same zoom-scaled rate plain E/Q uses, so
   * the camera→pivot vector is unchanged — OrbitControls.update() re-derives an
   * identical polar angle and radius, no clamp fires, and the view keeps
   * pointing exactly where it pointed while the ground recedes. Plain E/Q, by
   * contrast, moves only the eye and so swings it over a pinned pivot.
   *
   * The pivot's floor is the terrain (offset >= 0); the eye's own floor stays
   * with applyCameraFloor. A raise is a pure translation and needs no ground
   * data at all, so it works over an unloaded tile; a descent is rejected
   * outright when the sampler has none, because the floor cannot be verified
   * and a height is never fabricated (hard constraint #7). That sampler call is
   * the only ground query this adds, and only while descending — an idle frame
   * and a raise both spend nothing (hard constraint #17).
   */
  private applyExploreAltitude(dt: number): void {
    if (!this.exploreShiftHeld()) return;
    const up = this.keys.has('KeyE');
    const down = this.keys.has('KeyQ');
    if (up === down) return; // neither held, or both cancelling
    const t = this.orbitControls.target;
    const speed = explorePanSpeed(this.camera.position.distanceTo(t));
    if (down && this.groundSampler(t.x, t.z) === null) return; // unverifiable floor: don't descend
    const delta = exploreAltitudeDelta(this.exploreAltitudeOffsetM, (up ? 1 : -1) * speed * dt);
    if (delta === 0) return;
    this.exploreAltitudeOffsetM += delta;
    this.camera.position.y += delta;
    t.y += delta;
  }

  /**
   * Root-cause fix for issue #50: keep the OrbitControls pivot on the terrain
   * surface. The default target is (0,0,0) = 0 m AOD, but the ground at the
   * scene origin is ~170 m AOD, so an ungrounded pivot sits ~170 m
   * underground; tilting toward the horizon then arcs the camera down toward
   * that buried point and drives it below the surface (and the old naive floor
   * fought that every frame). Seating target.y on the ground makes normal tilt
   * arc the camera around the surface instead.
   *
   * Only samples when the target has moved horizontally (a pan) or has not yet
   * been grounded — idle orbit spends no extra sampleGround call. If the
   * sampler has no data (target outside loaded tiles, e.g. at startup before
   * tiles stream in) the target is left untouched and retried next frame:
   * never snap the pivot to a fabricated height (hard constraint #7).
   *
   * Camera position is deliberately left unchanged: at the top-down boot pose
   * the camera and target share x/z, so moving target.y along the shared
   * vertical axis leaves the rendered view pixel-identical (the look direction
   * stays straight down) — grounding the pivot does not disturb the initial
   * framing. Only the orbit radius (camera-to-pivot distance) tightens, which
   * only affects subsequent orbiting, not the current frame.
   */
  private seatOrbitTargetIfNeeded(): void {
    const t = this.orbitControls.target;
    if (t.x === this.orbitTargetGroundX && t.z === this.orbitTargetGroundZ) return;
    const ground = this.groundSampler(t.x, t.z);
    if (ground === null) return; // no data yet: leave the pivot, retry next frame
    // The second seating path (the truck is the other one), and it must agree
    // with it about where the pivot belongs: `ground + the Explore altitude
    // offset` (issue #184). Gated on the mode rather than applied blindly, so
    // Orbit's own pivot always seats on bare terrain and can never inherit a
    // lift left over from an Explore session.
    t.y = ground + (this.mode === 'explore' ? this.exploreAltitudeOffsetM : 0);
    this.orbitTargetGroundX = t.x;
    this.orbitTargetGroundZ = t.z;
  }

  /**
   * Terrain floor (issue #50): lifts the camera to at least ground +
   * MIN_FLY_CLEARANCE_M. Shared by fly and orbit. Exactly one sampleGround
   * call, and no allocation unless it actually has to lift the camera (a rare
   * genuine sub-terrain excursion). In orbit it lifts only the camera, not the
   * pivot: the pivot is already grounded by seatOrbitTargetIfNeeded, so lifting
   * it too would un-ground it and let the rig drift upward — the exact runaway
   * the first #50 attempt caused. Returns whether it moved the camera.
   */
  private applyCameraFloor(): boolean {
    const pos = this.camera.position;
    floorScratch.x = pos.x;
    floorScratch.y = pos.y;
    floorScratch.z = pos.z;
    const clamped = clampAboveGround(floorScratch, this.recordCameraGround, MIN_FLY_CLEARANCE_M);
    if (clamped === floorScratch) return false; // already above the floor / no ground data
    pos.y = clamped.y;
    return true;
  }

  /**
   * Drives the camera from routePlayer.advance() — position is pre-programmed
   * along the route (no WASD). The look direction is auto-aimed at the turbines
   * unless the viewer takes manual control with the arrow keys (yaw ←/→, pitch
   * ↑/↓), which switch the player into free-look (issue #5, rekeyed from
   * N/M/K/L to arrow keys in issue #67); the route Aim button re-engages the
   * auto-aim.
   */
  private updateRoute(dt: number): void {
    if (!this.routeConfig) return;
    const { player, polyline, arrayCentreTarget, turbineTargets, eyeHeightM, speedMS } = this.routeConfig;

    // Free-look rotation input; the first nudge engages free-look.
    const { dYaw, dPitch } = this.arrowLookDelta(dt);
    if (dYaw !== 0 || dPitch !== 0) player.nudgeFreeLook(dYaw, dPitch);

    const ctx: RouteAdvanceContext = {
      polyline,
      sampleGround: this.groundSampler,
      arrayCentreTarget,
      turbineTargets,
      eyeHeightM,
      speedMS,
    };
    const pose = player.advance(dt, ctx);
    this.yaw = pose.yaw;
    this.pitch = pose.pitch;
    this.applyRotation();
    this.camera.position.set(pose.x, pose.y, pose.z);
    this.camera.updateMatrixWorld();
  }

  private updateFly(dt: number): void {
    this.applyArrowLook(dt);
    this.applyRotation();
    forwardVec.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
    rightVec.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
    moveVec.set(0, 0, 0);
    if (this.keys.has('KeyW')) moveVec.add(forwardVec);
    if (this.keys.has('KeyS')) moveVec.sub(forwardVec);
    if (this.keys.has('KeyD')) moveVec.add(rightVec);
    if (this.keys.has('KeyA')) moveVec.sub(rightVec);
    if (this.keys.has('KeyE')) moveVec.y += 1;
    if (this.keys.has('KeyQ')) moveVec.y -= 1;
    let moved = false;
    if (moveVec.lengthSq() > 0) {
      moveVec.normalize();
      const sprint = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
      const speed = this.flySpeed * (sprint ? FLY_SPRINT_MULT : 1);
      this.camera.position.addScaledVector(moveVec, speed * dt);
      moved = true;
    }
    // Terrain floor (issue #50): checked every tick, not just on movement, so a
    // mode switch or teleport that lands below ground is corrected immediately
    // rather than only once WASD/QE is next pressed.
    const clampedByFloor = this.applyCameraFloor();
    if (moved || clampedByFloor) this.camera.updateMatrixWorld();
  }

  private updateWalk(dt: number): void {
    this.applyArrowLook(dt);
    this.applyRotation();
    const sinY = Math.sin(this.yaw);
    const cosY = Math.cos(this.yaw);
    // horizontal-only forward/right, yaw-plane (pitch never tilts walking)
    const fx = -sinY;
    const fz = -cosY;
    const rx = cosY;
    const rz = -sinY;
    walkForwardVec.set(fx, 0, fz);
    walkRightVec.set(rx, 0, rz);
    moveVec.set(0, 0, 0);
    if (this.keys.has('KeyW')) moveVec.add(walkForwardVec);
    if (this.keys.has('KeyS')) moveVec.sub(walkForwardVec);
    if (this.keys.has('KeyD')) moveVec.add(walkRightVec);
    if (this.keys.has('KeyA')) moveVec.sub(walkRightVec);
    if (moveVec.lengthSq() === 0) return;
    moveVec.normalize();
    const sprint = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    const speed = WALK_SPEED_M_S * (sprint ? WALK_SPRINT_MULT : 1);
    const dx = moveVec.x * speed * dt;
    const dz = moveVec.z * speed * dt;
    const next = walkStep(
      { x: this.camera.position.x, y: this.camera.position.y, z: this.camera.position.z },
      dx,
      dz,
      this.groundSampler,
    );
    this.camera.position.set(next.x, next.y, next.z);
    this.camera.updateMatrixWorld();
  }
}
